import type { ProgressSnapshot } from '@aigovops/core';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { tokens } from '@aigovops/design-system';
import { severityLabel } from '../../lib/format';

// Cluster 0 (PR-C0-1): recharts writes stroke/fill as SVG attributes,
// not CSS style, so `var(--...)` strings don't evaluate at paint. Use
// the TypeScript token object so the values resolve to concrete hex
// at bundle time. Locked to light-mode palette for v1; Cluster 1
// Dashboard rewire will thread a mode-aware ColorScheme through props
// once the runtime mode context is wired here.
const CHART_COLORS = {
  grid: tokens.colors.light.border,
  implemented: tokens.colors.light.success,
  open: tokens.colors.light.critical,
};

type Row = {
  severity: string;
  Open: number;
  Implemented: number;
};

export function BurnDownChart({ progress }: { progress: ProgressSnapshot }) {
  const rows: Row[] = (['critical', 'high', 'elevated', 'moderate', 'standard'] as const)
    .map((s) => ({
      severity: severityLabel[s],
      Open: progress.by_severity[s].open,
      Implemented: progress.by_severity[s].implemented,
    }))
    .filter((r) => r.Open + r.Implemented > 0);

  if (rows.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-8 text-center">
        No items to chart yet.
      </div>
    );
  }

  return (
    <div className="w-full h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} stackOffset="sign" margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
          <XAxis dataKey="severity" tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Implemented" stackId="a" fill={CHART_COLORS.implemented} radius={[0, 0, 0, 0]} />
          <Bar dataKey="Open" stackId="a" fill={CHART_COLORS.open} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
